import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { FiUser, FiEdit2, FiTrash2, FiCheck, FiX } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import {
  getAllDepartments,
  getServicesForDepartment,
  getAllServiceRequestActivities,
  getDefaultActivitiesForDepartment,
  normalizeServiceRequestActivity
} from '../utils/departmentMapping';

const DEPARTMENTS = getAllDepartments();
const SERVICE_ACTIVITIES = getAllServiceRequestActivities();
const DEPARTMENT_LABELS = {};

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'operator', label: 'Operator' },
  { value: 'department', label: 'Department' }
];

const TAB_OPTIONS = [
  { path: '/dashboard', label: 'Dashboard' },
  { path: '/chat', label: 'Live Chat' },
  { path: '/ai-chat', label: 'AI Assistant' },
  { path: '/guests', label: 'Guests' },
  { path: '/rooms', label: 'Rooms' },
  { path: '/analytics', label: 'Analytics' },
  { path: '/feedback', label: 'Feedback & Reviews' },
  { path: '/requests', label: 'Service Requests' },
  { path: '/critical-fixes', label: 'Critical Fixes' },
  { path: '/bookings', label: 'Activity & Bookings' },
  { path: '/slots', label: 'Slot Management' },
  { path: '/reminders', label: 'Reminders' },
  { path: '/qr', label: 'QR Rooms' },

  { path: '/users', label: 'Users' },
  { path: '/department-staff', label: 'Department Staff' },
  { path: '/settings', label: 'Settings' },
  { path: '/whitelabel', label: 'White Label' },
  { path: '/security/login-history', label: 'Security Login History' }
];

function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    designation: '',
    role: 'department',
    department: '',
    can_create_all_manual_requests: false,
    can_cancel_service_requests: false,
    allowed_activities: [],
    allowed_tabs: []
  });
  const [dynamicActivities, setDynamicActivities] = useState(SERVICE_ACTIVITIES);

  useEffect(() => {
    fetchUsers();
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    // Only show core request types, not every dynamic activity from the database
    const coreActivities = [
      'Housekeeping Service',
      'Maintenance Assistance',
      'IT Support',
      'Front Office',
      'Medical Emergency',
      'Security Assistance',
      'SPA Appointment',
      'Yoga & Wellness',
      'Salon'
    ];
    
    setDynamicActivities(coreActivities);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      alert('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't send empty password
        }
        await api.put(`/api/admin/users/${editingUser.id}`, updateData);
      } else {
        // Create user
        await api.post('/api/admin/users', formData);
      }
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      alert(error.response?.data?.error || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      designation: user.designation || '',
      role: user.role,
      department: user.department || '',
      can_create_all_manual_requests: Boolean(user.can_create_all_manual_requests),
      can_cancel_service_requests: Boolean(user.can_cancel_service_requests),
      allowed_activities: Array.isArray(user.allowed_activities)
        ? user.allowed_activities.map((activity) => normalizeServiceRequestActivity(activity))
        : [],
      allowed_tabs: Array.isArray(user.allowed_tabs) ? user.allowed_tabs : []
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    try {
      await api.delete(`/api/admin/users/${id}`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      designation: '',
      role: 'department',
      department: '',
      can_create_all_manual_requests: false,
      can_cancel_service_requests: false,
      allowed_activities: [],
      allowed_tabs: []
    });
    setEditingUser(null);
  };

  const toggleActivity = (activity) => {
    setFormData((prev) => {
      const exists = prev.allowed_activities.includes(activity);
      return {
        ...prev,
        allowed_activities: exists
          ? prev.allowed_activities.filter((item) => item !== activity)
          : [...prev.allowed_activities, activity]
      };
    });
  };

  const toggleTabAccess = (path) => {
    setFormData((prev) => {
      const exists = prev.allowed_tabs.includes(path);
      return {
        ...prev,
        allowed_tabs: exists ? prev.allowed_tabs.filter((item) => item !== path) : [...prev.allowed_tabs, path]
      };
    });
  };

  const applyDepartmentDefaults = () => {
    const defaults = getDefaultActivitiesForDepartment(formData.department);
    setFormData((prev) => ({ ...prev, allowed_activities: defaults }));
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      manager: 'bg-gray-100 text-gray-800',
      operator: 'bg-green-100 text-green-800',
      department: 'bg-purple-100 text-purple-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role] || 'bg-gray-100 text-gray-800'}`}>
        {role}
      </span>
    );
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const totalUsers = users.length;
  const activeUsers = users.filter((dashboardUser) => dashboardUser.is_active).length;
  const departmentUsers = users.filter((dashboardUser) => dashboardUser.role === 'department').length;
  const adminManagerUsers = users.filter((dashboardUser) => ['admin', 'manager'].includes(dashboardUser.role)).length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5 px-2 py-3 sm:px-3 sm:py-4 lg:px-1 lg:py-2">
        <div className="space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Dashboard Users</h1>
              <p className="text-sm text-gray-500 mt-1">Manage access, roles, and department assignments for your team.</p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors shadow-sm flex items-center font-semibold"
            >
              <FiUser className="mr-2" />
              Add User
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Users</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalUsers}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Active</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{activeUsers}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Department Users</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{departmentUsers}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Admin + Manager</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{adminManagerUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Password
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visible Activities
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visible Tabs
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="sticky right-0 z-20 bg-gray-50 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[-8px_0_12px_-10px_rgba(0,0,0,0.25)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-gray-100 border border-gray-200 text-gray-700 text-xs font-semibold flex items-center justify-center">
                        {getInitials(user.name)}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                        <div className="text-xs text-gray-500">ID: {user.id.slice(0, 8)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-700">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-700 font-mono">
                      {user.raw_password ? user.raw_password : <span className="text-gray-400 italic">(Encrypted)</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col items-start gap-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {getRoleBadge(user.role)}
                        {user.designation && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                            {user.designation}
                          </span>
                        )}
                      </div>
                      {user.can_cancel_service_requests && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                          Can Cancel Requests
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {user.department || '-'}
                    </div>
                    {user.department && user.role === 'department' && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {getServicesForDepartment(user.department).map((service) => (
                          <span
                            key={service}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {Array.isArray(user.allowed_activities) && user.allowed_activities.length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {user.allowed_activities.map((activity) => (
                          <span
                            key={`${user.id}-${activity}`}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100"
                          >
                            {normalizeServiceRequestActivity(activity)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">All activities</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {Array.isArray(user.allowed_tabs) && user.allowed_tabs.length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {user.allowed_tabs.map((tabPath) => {
                          const tab = TAB_OPTIONS.find((item) => item.path === tabPath);
                          return (
                            <span
                              key={`${user.id}-${tabPath}`}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100"
                            >
                              {tab?.label || tabPath}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">Default role tabs</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <FiCheck className="mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <FiX className="mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="sticky right-0 z-10 bg-white px-6 py-4 whitespace-nowrap text-right text-sm font-medium shadow-[-8px_0_12px_-10px_rgba(0,0,0,0.2)]">
                    <button
                      onClick={() => handleEdit(user)}
                      className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-800 mr-2"
                    >
                      <FiEdit2 />
                    </button>
                    {user.id !== currentUser?.id && (
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-900/50 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
            <div className="relative top-12 mx-auto border border-gray-200 w-full max-w-lg shadow-xl rounded-xl bg-white mb-10 overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingUser ? 'Edit User' : 'Create New User'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">Configure role access and department scope.</p>
              </div>

              <div className="p-6">
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 bg-gray-50 focus:bg-white transition-colors"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 bg-gray-50 focus:bg-white transition-colors"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password {editingUser && '(leave blank to keep current)'}
                    </label>
                    <input
                      type="password"
                      required={!editingUser}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 bg-gray-50 focus:bg-white transition-colors"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      required
                      value={formData.role}
                      onChange={(e) => {
                        const nextRole = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          role: nextRole,
                          department: nextRole !== 'department' ? '' : prev.department
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 bg-gray-50 focus:bg-white transition-colors"
                    >
                      {ROLES.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Designation <span className="text-xs text-gray-500">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. HOD, Duty Manager, General Manager"
                      value={formData.designation}
                      onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 bg-gray-50 focus:bg-white transition-colors"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Displayed next to their name in "Seen by" and profile views instead of the default department/role.
                    </p>
                  </div>

                  {formData.role === 'department' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Department <span className="text-xs text-gray-500">(Select to see assigned services)</span>
                      </label>
                      <select
                        required
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 bg-gray-50 focus:bg-white transition-colors"
                      >
                        <option value="">Select Department</option>
                        {DEPARTMENTS.map((dept) => (
                          <option key={dept} value={dept}>
                            {DEPARTMENT_LABELS[dept] || dept}
                          </option>
                        ))}
                      </select>
                      {formData.department && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-xs font-medium text-gray-900 mb-1">This department can access:</p>
                          <div className="flex flex-wrap gap-2">
                            {getServicesForDepartment(formData.department).map((service) => (
                              <span
                                key={service}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                          {getServicesForDepartment(formData.department).length === 0 && (
                            <p className="text-xs text-gray-600">No services assigned</p>
                          )}
                          <button
                            type="button"
                            onClick={applyDepartmentDefaults}
                            className="mt-3 text-xs font-semibold text-gray-700 underline"
                          >
                            Use recommended activity visibility for this department
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mb-4 p-3 border border-indigo-100 rounded-lg bg-indigo-50/50">
                    <label className="flex items-start gap-2.5 text-sm text-gray-800 font-semibold cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={Boolean(formData.can_create_all_manual_requests)}
                        onChange={(e) => setFormData((prev) => ({ ...prev, can_create_all_manual_requests: e.target.checked }))}
                        className="h-4 w-4 mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div>
                        <span>Allow creating manual requests for all departments</span>
                        <p className="text-xs font-normal text-gray-500 mt-0.5">
                          When checked, this user can create manual requests for any department from their dashboard without restrictions.
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="mb-4 p-3 border border-rose-100 rounded-lg bg-rose-50/50">
                    <label className="flex items-start gap-2.5 text-sm text-gray-800 font-semibold cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={Boolean(formData.can_cancel_service_requests)}
                        onChange={(e) => setFormData((prev) => ({ ...prev, can_cancel_service_requests: e.target.checked }))}
                        className="h-4 w-4 mt-0.5 rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                      />
                      <div>
                        <span>Allow cancelling service requests</span>
                        <p className="text-xs font-normal text-gray-500 mt-0.5">
                          When checked, this user will have permission to cancel service requests (a cancellation reason is required upon cancelling).
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Visible Service Request Activities
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto p-3 border border-gray-200 rounded-lg bg-gray-50">
                      {dynamicActivities.map((activity) => {
                        const checked = formData.allowed_activities.includes(activity);
                        const displayLabel = DEPARTMENT_LABELS[activity] || activity;
                        return (
                          <label key={activity} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleActivity(activity)}
                              className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-300"
                            />
                            <span>{displayLabel}</span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      If none are selected, this user will see all activities allowed by role/department defaults.
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sidebar / Topbar Tabs for this Login
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto p-3 border border-gray-200 rounded-lg bg-gray-50">
                      {TAB_OPTIONS.map((tab) => {
                        const checked = formData.allowed_tabs.includes(tab.path);
                        return (
                          <label key={tab.path} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleTabAccess(tab.path)}
                              className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-300"
                            />
                            <span>{tab.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      If none are selected, default tabs from role/department rules will be shown.
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors shadow-sm font-semibold"
                    >
                      {editingUser ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Users;

